// src/features/tienda/use-active-store.ts
import { useEffect } from 'react';
import { create } from 'zustand';
import type { MyStore } from '@/api/schemas/tienda';

const STORAGE_KEY = 'ko-admin.tienda.active';

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null) {
  try {
    if (id === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // storage unavailable (tests, private mode): the choice lives in memory
  }
}

interface ActiveStoreState {
  /** The store the owner chose; `null` until they choose or until the list answers. */
  activeId: string | null;
  setActiveId: (id: string | null) => void;
}

/**
 * Which of the owner's stores the pages are about (2026-11-02: one owner,
 * several stores). One choice for the whole panel — the switcher in the top
 * bar and every «Mi tienda» page read the same id, and it survives a reload.
 */
export const useActiveStoreStore = create<ActiveStoreState>((set) => ({
  activeId: readStored(),
  setActiveId: (id) => {
    writeStored(id);
    set({ activeId: id });
  },
}));

/**
 * The active store out of `stores`: the chosen one when it is still in the
 * list, else the first (the primary store, as the backend orders them). A
 * choice that no longer resolves — a store taken down — is dropped.
 */
export function useActiveStore(stores: MyStore[]): [MyStore | null, (id: string) => void] {
  const activeId = useActiveStoreStore((s) => s.activeId);
  const setActiveId = useActiveStoreStore((s) => s.setActiveId);
  const active = stores.find((s) => s.id === activeId) ?? stores[0] ?? null;
  useEffect(() => {
    if (active && active.id !== activeId) setActiveId(active.id);
  }, [active, activeId, setActiveId]);
  return [active, setActiveId];
}
